/**
 * Guards plan/usage; triggers PlanUpgradeDialog when no plan, expired, or limit exceeded.
 * Uses module-level callbacks (GlobalDialogs registers them) because hooks can't render dialogs.
 * Architectural Context: Feature guard. Use with useGuardedAction or direct checkUsageLimit calls.
 *
 * @returns checkUsageLimit(params) → { allowed, limit, used, remaining } or null
 * @returns isLoading, planSummary
 */

import { useCallback } from 'react';
import { useMyPlanSummary } from './useUsage';
import type { JobNotificationEvent } from './useJobNotifications';

export type UpgradeDialogReason = 'plan_expired' | 'usage_limit_exceeded' | 'no_plan';

let openUpgradeDialogCallback: ((params: {
  metric: 'cv' | 'interview';
  limit: number;
  used: number;
  origin?: string;
  reason?: UpgradeDialogReason;
}) => void) | null = null;

let handleUsageLimitExceededCallback: ((event: JobNotificationEvent) => void) | null = null;

export const setUpgradeDialogCallback = (callback: typeof openUpgradeDialogCallback) => {
  openUpgradeDialogCallback = callback;
};

export const setUpgradeModalCallback = setUpgradeDialogCallback;

export const setUsageLimitExceededHandler = (handler: typeof handleUsageLimitExceededCallback) => {
  handleUsageLimitExceededCallback = handler;
};

export const getUpgradeDialogCallback = (): typeof openUpgradeDialogCallback => {
  return openUpgradeDialogCallback;
};

export const getUpgradeModalCallback = getUpgradeDialogCallback;

export const getUsageLimitExceededHandler = (): typeof handleUsageLimitExceededCallback => {
  return handleUsageLimitExceededCallback;
};

export type UsageMetric = 'cv' | 'interview';

interface CheckUsageLimitParams {
  metric: UsageMetric;
  quantity?: number;
  origin?: string;
}

interface CheckUsageLimitResult {
  allowed: boolean;
  limit: number;
  used: number;
  remaining: number;
}

export const usePlanUsageGuard = () => {
  const { data: planSummary, isLoading } = useMyPlanSummary();

  const checkUsageLimit = useCallback((
    params: CheckUsageLimitParams
  ): CheckUsageLimitResult | null => {
    const { metric, quantity = 1, origin } = params;

    if (isLoading || !planSummary) return null;

    if (!planSummary.has_plan) {
      openUpgradeDialogCallback?.({
        metric,
        limit: 0,
        used: 0,
        origin,
        reason: 'no_plan',
      });
      return {
        allowed: false,
        limit: 0,
        used: 0,
        remaining: 0,
      };
    }

    if (planSummary.expired) {
      openUpgradeDialogCallback?.({
        metric,
        limit: planSummary.limits[metric],
        used: planSummary.usage[metric],
        origin,
        reason: 'plan_expired',
      });
      return {
        allowed: false,
        limit: planSummary.limits[metric],
        used: planSummary.usage[metric],
        remaining: 0,
      };
    }

    const limit = planSummary.limits[metric];
    const used = planSummary.usage[metric];
    const remaining = planSummary.remaining[metric];

    const isUnlimited = limit === -1 || remaining === -1; // -1 = unlimited
    const hasCapacity = isUnlimited || (remaining !== null && remaining !== undefined && remaining >= quantity);

    if (!hasCapacity) {
      openUpgradeDialogCallback?.({
        metric,
        limit,
        used,
        origin,
        reason: 'usage_limit_exceeded',
      });
      return {
        allowed: false,
        limit,
        used,
        remaining,
      };
    }

    return {
      allowed: true,
      limit,
      used,
      remaining,
    };
  }, [planSummary, isLoading]);

  return {
    checkUsageLimit,
    isLoading,
    planSummary,
  };
};
